import { useState, useEffect, useRef } from 'react';

// Persists a piece of state to sessionStorage under `key`, so leaving a vertical (e.g. opening
// a detail page or navigating away) and coming back restores the exact search + results.
// Each vertical uses its own namespaced keys, so switching verticals naturally shows that
// vertical's own last state and never clears another vertical's search.
export function usePersistentState(key, initial) {
  const [state, setState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw != null ? JSON.parse(raw) : initial;
    } catch {
      return initial;
    }
  });
  const keyRef = useRef(key);
  keyRef.current = key;
  useEffect(() => {
    try {
      if (state === undefined || state === null) sessionStorage.removeItem(keyRef.current);
      else sessionStorage.setItem(keyRef.current, JSON.stringify(state));
    } catch {
      /* storage full / unavailable — non-fatal */
    }
  }, [state]);
  return [state, setState];
}

// Clear all persisted keys for a vertical prefix (used by an explicit "clear/new search").
export function clearPersisted(prefix) {
  try {
    Object.keys(sessionStorage).filter((k) => k.startsWith(prefix)).forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* non-fatal */
  }
}
